import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthStore, api } from "@/store";
import { useColWidths } from "@/hooks/useColWidths";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ArrowLeft, Loader2, FunctionSquare, LockKeyhole, Pencil, Save, Plus
} from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n/bg";

function getColLetter(col) {
  let s = "", c = col;
  while (c >= 0) { s = String.fromCharCode((c % 26) + 65) + s; c = Math.floor(c / 26) - 1; }
  return s;
}

export default function TemplateEditorPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.isAdmin);

  const [template, setTemplate] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cellDialog, setCellDialog] = useState(null);
  const [addRowCount, setAddRowCount] = useState(1);
  const gridRef = useRef(null);
  const { handleResizeStart, getColStyle } = useColWidths(
    templateId ? `template:${templateId}` : null, activeSheet
  );

  useEffect(() => { loadTemplate(); }, [templateId]);
  useEffect(() => { if (activeSheet) loadSheet(activeSheet); }, [activeSheet]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/templates/${templateId}`);
      setTemplate(res.data);
      setSheets(res.data.sheets || []);
      if (res.data.sheets?.length > 0) setActiveSheet(res.data.sheets[0].sheet_name);
    } catch { toast.error(t("loadFailed")); navigate("/templates"); }
    finally { setLoading(false); }
  };

  const loadSheet = async (sheetName) => {
    try {
      setLoading(true);
      setCellDialog(null);
      const res = await api.get(`/templates/${templateId}/sheets/${encodeURIComponent(sheetName)}/cells`);
      setCells(res.data.cells || []);
    } catch { toast.error(t("loadFailed")); }
    finally { setLoading(false); }
  };

  const handleCellClick = (cell) => {
    if (!cell) return;
    setCellDialog({
      cell,
      value: cell.default_value ?? cell.computed_value ?? "",
      formula: cell.formula ?? "",
    });
  };

  const handleCellSave = async () => {
    if (!cellDialog) return;
    const { cell, value, formula } = cellDialog;
    setSaving(true);
    try {
      await api.patch(`/templates/${templateId}/cells`, {
        sheet_name: activeSheet,
        address: cell.address,
        value: value || null,
        formula: formula || null,
      });
      // Update local state
      setCells(prev => prev.map(c => {
        if (c.address === cell.address) {
          const isF = !!formula && formula.startsWith("=");
          return { ...c, default_value: value, formula: formula || c.formula, is_formula: isF, computed_value: value };
        }
        return c;
      }));
      setCellDialog(null);
      toast.success(`${cell.address} ${t("cellSaved")}`);
    } catch (err) { toast.error(err.response?.data?.detail || t("saveFailed")); }
    finally { setSaving(false); }
  };

  const handleAddRows = async () => {
    if (addRowCount < 1) return;
    setSaving(true);
    try {
      const res = await api.post(`/templates/${templateId}/sheets/${encodeURIComponent(activeSheet)}/add-rows`, { count: addRowCount });
      toast.success(`${t("rowsAdded")}: +${res.data.rows_added}`);
      loadSheet(activeSheet);
    } catch (err) { toast.error(err.response?.data?.detail || t("failed")); }
    finally { setSaving(false); }
  };

  if (loading && !template) return (
    <div className="flex items-center justify-center h-screen bg-[#f8fafc]">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  const maxRow = cells.length > 0 ? Math.max(...cells.map(c => c.row)) : 0;
  const maxCol = cells.length > 0 ? Math.max(...cells.map(c => c.col)) : 0;
  const cellMap = {};
  cells.forEach(c => { cellMap[`${c.row},${c.col}`] = c; });

  return (
    <div data-testid="template-editor" className="flex flex-col h-screen bg-[#f8fafc]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-slate-200 shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/templates")} className="h-8 text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t("back")}
          </Button>
          <div className="h-5 w-px bg-slate-200" />
          <Pencil className="w-4 h-4 text-[#14532d]" />
          <span className="text-sm font-semibold text-[#14532d]">{t("templateEditor")}</span>
          <span className="text-sm text-slate-600">{template?.name}</span>
          {saving && <Loader2 className="w-4 h-4 animate-spin text-amber-500" />}
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="add-row-count" type="number" min={1} max={50} value={addRowCount}
            onChange={e => setAddRowCount(Math.max(1, parseInt(e.target.value) || 1))}
            className="h-8 w-16 text-xs text-center" />
          <Button data-testid="add-rows-button" variant="outline" size="sm" onClick={handleAddRows}
            disabled={saving} className="h-8 text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> {t("addRows")}
          </Button>
        </div>
      </div>

      {/* Sheet tabs */}
      <div className="flex items-center bg-white border-b border-slate-200 px-2 overflow-x-auto shrink-0">
        {sheets.map(s => (
          <button key={s.sheet_name} data-testid={`te-sheet-${s.order_index}`}
            onClick={() => setActiveSheet(s.sheet_name)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeSheet === s.sheet_name ? "border-[#14532d] text-[#14532d] bg-emerald-50/50" : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}>{s.sheet_name}</button>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-1.5 bg-white border-b border-slate-100 text-[10px] text-slate-500 shrink-0">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-emerald-300 bg-emerald-50 inline-block" /> {t("editableInput")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-slate-200 bg-slate-100 inline-block" /> {t("formulaAutoCalc")}</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded border border-amber-200 bg-amber-50/50 inline-block" /> {t("protectedPreset")}</span>
        <span className="text-slate-400 ml-2">{t("templateEditorDesc")}</span>
      </div>

      {/* Grid */}
      <div data-testid="template-editor-grid" className="flex-1 overflow-auto" ref={gridRef}>
        <TooltipProvider delayDuration={200}>
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <table className="border-collapse text-xs" style={{ fontFamily: "'Calibri', 'Segoe UI', sans-serif", tableLayout: "fixed" }}>
            <thead className="sticky top-0 z-10">
              <tr>
                <th className="bg-slate-100 border border-slate-200 px-1 py-1 text-center text-slate-400 font-normal w-10 min-w-[40px]">#</th>
                {Array.from({ length: maxCol }, (_, i) => (
                  <th key={i} className="bg-slate-100 border border-slate-200 px-2 py-1 text-center text-slate-500 font-semibold relative select-none"
                    style={{ fontSize: "11px", ...getColStyle(i) }}>
                    {getColLetter(i)}
                    <div onMouseDown={(e) => handleResizeStart(i, e)}
                      className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-[#14532d]/30 active:bg-[#14532d]/50"
                      style={{ touchAction: "none" }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxRow }, (_, ri) => {
                const row = ri + 1;
                return (
                  <tr key={row}>
                    <td className="bg-slate-50 border border-slate-200 px-1 py-0.5 text-center text-slate-400 font-normal" style={{ fontSize: "10px" }}>{row}</td>
                    {Array.from({ length: maxCol }, (_, ci) => {
                      const col = ci + 1;
                      const cell = cellMap[`${row},${col}`];
                      let bg = "#ffffff", color = "#1e293b", tip = "";

                      if (cell) {
                        if (cell.is_input) { bg = "#ecfdf5"; }
                        else if (cell.is_formula) { bg = "#f1f5f9"; color = "#475569"; tip = cell.formula || ""; }
                        else if (cell.has_preset_value) { bg = "#fffbeb"; color = "#78716c"; }
                        if (cell.style?.bold) color = "#0f172a";
                      }

                      let dv = cell?.default_value ?? cell?.computed_value ?? "";
                      if (dv === "None") dv = "";
                      if (typeof dv === "string" && dv !== "" && !isNaN(dv)) {
                        const n = parseFloat(dv);
                        if (!isNaN(n)) dv = Number.isInteger(n) ? n.toLocaleString("bg-BG") : n.toLocaleString("bg-BG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      }

                      const td = (
                        <td key={col} data-testid={cell ? `te-cell-${cell.address}` : undefined}
                          onClick={() => handleCellClick(cell)}
                          className="border border-slate-200 px-1.5 py-0.5 relative cursor-pointer hover:ring-1 hover:ring-[#14532d] hover:ring-inset"
                          style={{
                            backgroundColor: bg, color,
                            fontWeight: cell?.style?.bold ? "700" : "normal",
                            textAlign: (cell && !isNaN(cell.default_value) && cell.default_value !== "" && cell.default_value !== null && cell.default_value !== "None") ? "right" : "left",
                            minWidth: "80px", maxWidth: "200px",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}
                        >
                          {dv}
                          {cell?.is_formula && <FunctionSquare className="w-2.5 h-2.5 absolute top-0 right-0 text-slate-300" />}
                          {cell?.has_preset_value && !cell?.is_formula && !cell?.is_input && <LockKeyhole className="w-2.5 h-2.5 absolute top-0 right-0 text-amber-300" />}
                        </td>
                      );

                      if (tip) {
                        return (
                          <Tooltip key={col}>
                            <TooltipTrigger asChild>{td}</TooltipTrigger>
                            <TooltipContent side="top" className="text-[10px] max-w-[250px] font-mono">{tip}</TooltipContent>
                          </Tooltip>
                        );
                      }
                      return td;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </TooltipProvider>
      </div>

      {/* Cell Edit Dialog */}
      <Dialog open={!!cellDialog} onOpenChange={open => { if (!open) setCellDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Pencil className="w-4 h-4 text-[#14532d]" />
              <span className="font-mono">{activeSheet}!{cellDialog?.cell?.address}</span>
            </DialogTitle>
          </DialogHeader>
          {cellDialog && (
            <div className="space-y-4 pt-1">
              {cellDialog.cell.is_formula && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
                  <FunctionSquare className="w-3 h-3 mr-1" /> {t("formula")}
                </span>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">{t("cellValue")}</Label>
                <Input data-testid="te-cell-value" value={cellDialog.value}
                  onChange={e => setCellDialog(prev => ({ ...prev, value: e.target.value }))}
                  className="h-9 text-sm font-mono" placeholder="..." />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">
                  <FunctionSquare className="w-3 h-3 inline mr-1" />{t("formulaLabel")}
                </Label>
                <Input data-testid="te-cell-formula" value={cellDialog.formula}
                  onChange={e => setCellDialog(prev => ({ ...prev, formula: e.target.value }))}
                  className="h-9 text-sm font-mono" placeholder="=SUM(A1:A10)" />
                <p className="text-[10px] text-slate-400">{t("formulaHint")}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCellDialog(null)}>{t("close")}</Button>
            <Button data-testid="te-cell-save" size="sm" onClick={handleCellSave} disabled={saving}
              className="bg-[#14532d] hover:bg-[#166534] text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
